import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  InfoWindow,
  Map,
  Marker,
  useMap,
} from "@vis.gl/react-google-maps";
import {
  Compass,
  MapPin,
  Clock,
  Banknote,
  Navigation,
  Maximize2,
  X,
} from "lucide-react";
import type { Day, ItineraryItem } from "@/context/JourneyContext";
import { TYPE_ICON, BUCKET_LABELS } from "@/lib/itinerary-display";

// Pin colours per the artboards' map placeholder: a stop is ink with a
// paper stroke and label, and the selected stop is the accent with the
// on-accent label. Google Maps icons take literal colours, not CSS
// variables, and the basemap is light in both themes, so these are the
// light-theme Field Notes tokens.
const PIN_FILL = "#1a1815"; // --fn-ink
const PIN_FILL_SELECTED = "#2f9e6e"; // --fn-accent
const PIN_STROKE = "#faf9f6"; // --fn-paper
const PIN_LABEL = "#faf9f6"; // --fn-paper
const PIN_LABEL_SELECTED = "#ffffff"; // --fn-on-accent

// Teardrop pin path (Material "place" glyph outline), drawn so its tip
// sits at (0,0). We anchor the marker at that tip and offset the number
// label up into the round head. Using an SVG path instead of the flat
// circle gives the markers a bolder, map-pin silhouette with a drop
// shadow, so they read as real destinations rather than dots.
const PIN_PATH =
  "M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z";

const FitBoundsOnLoad = ({
  positions,
}: {
  positions: { lat: number; lng: number }[];
}) => {
  const map = useMap();
  // Keyed on the coordinates themselves, not on the array. The parent
  // rebuilds `positions` on every render, so an identity dependency refit
  // the bounds whenever anything in the trip changed (selecting a pin, a
  // concierge stream tick) and threw away whatever the reader had panned
  // or zoomed to. Now it fits when the stops actually change, and once
  // per map, which is what the name always claimed.
  const key = positions.map((p) => `${p.lat},${p.lng}`).join("|");
  useEffect(() => {
    if (!map || positions.length === 0) return;
    const valid = positions.filter((p) => p.lat !== 0 || p.lng !== 0);
    if (valid.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    valid.forEach((pos) => bounds.extend(pos));
    map.fitBounds(bounds, 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key]);
  return null;
};

// Renders the numbered teardrop pins. Split into its own component that
// reads useMap() and bails until the map (and therefore the `google`
// global) exists, so constructing google.maps.Point is always safe.
const TripMarkers = ({
  items,
  expandedItemId,
  onPinClick,
}: {
  items: ItineraryItem[];
  expandedItemId: string | null;
  onPinClick: (itemId: string) => void;
}) => {
  const map = useMap();
  if (!map || typeof google === "undefined") return null;
  return (
    <>
      {items.map((item, idx) => {
        const isSelected = item.id === expandedItemId;
        const scale = isSelected ? 1.7 : 1.3;
        return (
          <Marker
            key={item.id}
            position={item.coordinates}
            onClick={() => onPinClick(item.id)}
            zIndex={isSelected ? 1000 : idx}
            icon={{
              path: PIN_PATH,
              fillColor: isSelected ? PIN_FILL_SELECTED : PIN_FILL,
              fillOpacity: 1,
              strokeColor: PIN_STROKE,
              strokeWeight: 2,
              scale,
              // Anchor at the teardrop tip; label in the round head.
              anchor: new google.maps.Point(12, 36),
              labelOrigin: new google.maps.Point(12, 13),
            }}
            label={{
              text: String(idx + 1),
              color: isSelected ? PIN_LABEL_SELECTED : PIN_LABEL,
              fontSize: isSelected ? "13px" : "11px",
              fontWeight: "700",
            }}
            title={item.title}
          />
        );
      })}
    </>
  );
};

// Builds a Google Maps URL that opens ONE specific place: the listing
// when we know which one, otherwise a pin on the stop's own coordinates.
// Never a route. A card's title is prose, not a search term, so feeding it
// to Maps as free text is what turned "Gion walk" into a walking route and
// "Dinner in Gion" into a search box full of the card's own words.
export const buildMapsPlaceUrl = (
  item: ItineraryItem,
  destination?: string
): string => {
  const search = "https://www.google.com/maps/search/?api=1&query=";
  const title = item.title?.trim();
  const lat = item.coordinates?.lat;
  const lng = item.coordinates?.lng;
  // (0, 0) is the frontend's "not geocoded yet" placeholder, not the
  // Atlantic, so it never counts as a location.
  const hasCoordinates =
    typeof lat === "number" &&
    typeof lng === "number" &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    (lat !== 0 || lng !== 0);

  // The exact listing: photos, hours, reviews. query is only the label
  // Maps shows while it resolves the id.
  if (item.googlePlaceId) {
    const label = title || (hasCoordinates ? `${lat},${lng}` : destination || "");
    return `${search}${encodeURIComponent(label)}&query_place_id=${encodeURIComponent(item.googlePlaceId)}`;
  }

  // No id, but a real address: Maps resolves an address to the thing that
  // stands at it, so this shows the restaurant rather than an anonymous
  // dropped pin. An address is also not a phrase, so it cannot be read as
  // a route the way a title like "Gion walk" was.
  const address = item.address?.trim();
  if (address) return `${search}${encodeURIComponent(address)}`;

  // Neither: drop a pin exactly where the stop is.
  if (hasCoordinates) return `${search}${lat},${lng}`;

  // Nothing but words. Last resort, and the only branch that can still
  // land on a search results page.
  const query = [title, destination?.trim()].filter(Boolean).join(", ");
  return `${search}${encodeURIComponent(query || "")}`;
};

interface TripMapProps {
  days: Day[];
  destination?: string;
  destinationCenter: { lat: number; lng: number } | null;
  expandedItemId: string | null;
  onPinClick: (itemId: string) => void;
  onClearSelection: () => void;
}

const GOOGLE_MAPS_API_KEY: string =
  import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? "";

export const TripMap = ({
  days,
  destination,
  destinationCenter,
  expandedItemId,
  onPinClick,
  onClearSelection,
}: TripMapProps) => {
  // Our own full-screen view, rather than Google's fullscreen control.
  // The browser Fullscreen API is unavailable in an installed iOS PWA, so
  // the native button had to be hidden there, which left the installed app
  // with no way to enlarge the map at all. This is a plain overlay, so it
  // works identically in the browser and on the home screen.
  const [isFullScreen, setIsFullScreen] = useState(false);

  // While the overlay is up, Escape closes it and the page behind it stays
  // put instead of scrolling under the map.
  useEffect(() => {
    if (!isFullScreen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsFullScreen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isFullScreen]);

  const allItems = days.flatMap((d) => d.items);
  const validItems = allItems.filter(
    (i) => i.coordinates.lat !== 0 || i.coordinates.lng !== 0
  );
  const positions = validItems.map((i) => i.coordinates);
  const selectedItem = validItems.find((i) => i.id === expandedItemId) ?? null;

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="h-full flex items-center justify-center p-6 text-center">
        <p className="text-sm text-ink-muted">
          Map unavailable. Set{" "}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">
            VITE_GOOGLE_MAPS_API_KEY
          </code>{" "}
          in your environment.
        </p>
      </div>
    );
  }

  if (validItems.length === 0 && !destinationCenter) {
    return (
      <div className="h-full flex items-center justify-center p-8 text-center bg-muted/20">
        <p className="text-sm text-ink-muted max-w-xs leading-relaxed">
          Pins will appear here once we've added stops to your trip.
        </p>
      </div>
    );
  }

  const fallbackCenter =
    positions[0] ?? destinationCenter ?? { lat: 38.7223, lng: -9.1393 };
  const fallbackZoom = validItems.length > 0 ? 12 : 11;

  const mapView = (
    <Map
      defaultCenter={fallbackCenter}
      defaultZoom={fallbackZoom}
      // No reuseMaps here, deliberately. Expanding remounts the map, so the
      // library's instance cache looks like an easy saving, but it hands
      // back whatever state the previous instance was in, and it could not
      // be proved safe here: the desktop Map tab renders an empty box on
      // main already, for a reason that predates this control, so there is
      // no working second mount to test the cache against. A fresh map per
      // toggle is the honest cost until that is fixed.
      gestureHandling="greedy"
      disableDefaultUI={false}
      // Ours is the only way to enlarge the map, so Google's own control
      // never appears. See the isFullScreen comment above.
      fullscreenControl={false}
      onClick={onClearSelection}
      style={{ width: "100%", height: "100%" }}
    >
      <FitBoundsOnLoad positions={positions} />
      <TripMarkers
        items={validItems}
        expandedItemId={expandedItemId}
        onPinClick={onPinClick}
      />
      {selectedItem && (
        <InfoWindow
          position={selectedItem.coordinates}
          onCloseClick={onClearSelection}
        >
          <MarkerInfoCard item={selectedItem} destination={destination} />
        </InfoWindow>
      )}
    </Map>
  );

  // The map lives in one place at a time: inline, or in the overlay.
  if (isFullScreen) {
    return createPortal(
      <div className="fixed inset-0 z-[70] bg-paper flex flex-col">
        <div className="flex items-center gap-2.5 py-3 pl-[22px] pr-3 border-b border-ink shrink-0">
          <MapPin className="h-4 w-4 text-accent shrink-0" />
          <span className="font-sans text-[11px] font-bold tracking-[0.2em] uppercase text-ink shrink-0">
            Map
          </span>
          {destination && (
            <span className="flex-1 min-w-0 truncate font-serif italic text-[12.5px] text-ink-faint">
              {validItems.length} {validItems.length === 1 ? "stop" : "stops"} ·{" "}
              {destination}
            </span>
          )}
          <button
            onClick={() => setIsFullScreen(false)}
            aria-label="Close map"
            className="h-11 w-11 ml-auto inline-flex items-center justify-center text-ink"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>
        <div className="flex-1 min-h-0">{mapView}</div>
      </div>,
      document.body
    );
  }

  return (
    <div className="relative h-full w-full">
      {mapView}
      {/* 44px target holding a 34px face, the same way the sheet headers
          and card actions carry their icons. */}
      <button
        onClick={() => setIsFullScreen(true)}
        aria-label="Expand map"
        className="absolute top-0.5 right-0.5 z-[1] h-11 w-11 inline-flex items-center justify-center"
      >
        <span className="h-[34px] w-[34px] inline-flex items-center justify-center border border-rule bg-paper text-ink shadow-[0_2px_6px_-2px_rgba(var(--fn-shadow-rgb),0.4)]">
          <Maximize2 className="h-[15px] w-[15px]" />
        </span>
      </button>
    </div>
  );
};

const MarkerInfoCard = ({
  item,
  destination,
}: {
  item: ItineraryItem;
  destination?: string;
}) => {
  const Icon = TYPE_ICON[item.type] ?? Compass;
  const timeLabel = item.specificTime || BUCKET_LABELS[item.timeBucket];
  return (
    <div className="min-w-[200px] max-w-[260px] py-1 pr-1">
      <div className="flex items-start gap-2">
        <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0 text-accent-text" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm leading-tight text-gray-900">
            {item.title}
          </div>
          {timeLabel && (
            <div className="text-[11px] text-gray-500 mt-0.5">{timeLabel}</div>
          )}
        </div>
      </div>
      {item.address && (
        <div className="mt-2 text-xs text-gray-600 flex items-start gap-1.5">
          <MapPin className="h-3 w-3 mt-0.5 shrink-0 text-gray-400" />
          <span className="leading-snug">{item.address}</span>
        </div>
      )}
      {(item.hours || item.priceRange) && (
        <div className="mt-1 text-xs text-gray-500 flex gap-2 flex-wrap">
          {item.hours && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {item.hours}
            </span>
          )}
          {item.priceRange && (
            <span className="flex items-center gap-1">
              <Banknote className="h-3 w-3" />
              {item.priceRange}
            </span>
          )}
        </div>
      )}
      <a
        href={buildMapsPlaceUrl(item, destination)}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-medium text-accent-text hover:text-accent-hover transition-colors"
      >
        <Navigation className="h-3 w-3" />
        Open in Google Maps
      </a>
    </div>
  );
};
